import { gsap } from 'gsap';
import { ScrollProgressCalculator } from './ScrollProgressCalculator';

/**
 * Advanced Triggers - ENHANCED with Smart "Once" Logic - NAPRAWIONA WERSJA
 * ✅ FIXED: executeReverseAnimation używa teraz unified system
 * ✅ FIXED: Wszystkie discrete triggery prawidłowo resetują do initial state
 * ✅ FIXED: Intersection, Hover, Click działają z wszystkimi propertiami (nie tylko opacity)
 * ✅ "Once" only for intersection trigger
 * ❌ "Once" removed from scroll (has animation area logic)
 * ✅ Hover/Click have their own repeat control options
 * ✅ Mouse position delegated to mobile-aware system
 */

// Global mouse position tracking (legacy compatibility)
let globalMousePosition = { 
    x: typeof window !== 'undefined' ? window.innerWidth / 2 : 0, 
    y: typeof window !== 'undefined' ? window.innerHeight / 2 : 0 
};

let globalMouseTrackingInitialized = false;

/**
 * Initialize global mouse tracking (legacy compatibility)
 */
function initGlobalMouseTracking() {
    if (globalMouseTrackingInitialized) return;
    
    globalMousePosition.x = window.innerWidth / 2;
    globalMousePosition.y = window.innerHeight / 2;
    
    document.addEventListener('mousemove', (e) => {
        globalMousePosition.x = e.clientX;
        globalMousePosition.y = e.clientY;
    }, { passive: true });
    
    globalMouseTrackingInitialized = true;
    // console.log('🖱️ Global mouse tracking initialized');
}

/**
 * Execute animation using custom function or fallback
 * @param {Element} element - DOM element
 * @param {Object} animationConfig - Animation configuration
 * @returns {gsap.Timeline} Animation timeline
 */
function executeAnimation(element, animationConfig) {
    if (animationConfig.customAnimationFunction) {
        // Use custom function (usually from unified system)
        return animationConfig.customAnimationFunction();
    }
    
    // Fallback to simple animation
    const timeline = gsap.timeline();
    
    timeline.to(element, {
        opacity: 1,
        duration: animationConfig.duration || 1,
        delay: animationConfig.delay || 0,
        ease: animationConfig.ease || 'power2.out'
    });
    
    return timeline;
}

/**
 * ✅ FIXED: Execute reverse animation using unified system
 * @param {Element} element - DOM element  
 * @param {Object} animationConfig - Animation configuration
 * @returns {gsap.Timeline} Reverse animation timeline
 */
function executeReverseAnimation(element, animationConfig) {
    // ✅ CRITICAL FIX: Use unified system if available
    if (animationConfig.customReverseAnimationFunction) {
        // console.log(`🔄 Using custom reverse animation function for ${element.id}`);
        return animationConfig.customReverseAnimationFunction();
    }
    
    // ✅ CRITICAL FIX: Try to use unified system directly
    if (window.animationSystem) {
        const elementData = window.animationSystem.elements.get(element);
        if (elementData) {
            // console.log(`🔄 Using unified system for reverse animation: ${element.id}`);
            animateElementToInitial(elementData);
            return gsap.timeline(); // Return empty timeline for compatibility
        }
    }
    
    // ✅ ENHANCED FALLBACK: Instead of hardcoded opacity, try to reset common properties
    // console.log(`⚠️ Using enhanced fallback reverse animation for ${element.id}`);
    const timeline = gsap.timeline();
    
    // Get element's initial computed styles or reasonable defaults
    const computedStyle = window.getComputedStyle(element);
    const resetProps = {
        opacity: 1, // Reset to visible
        duration: (animationConfig.duration || 1) * 0.5,
        ease: animationConfig.ease || 'power2.out'
    };
    
    // Try to reset transform-related properties
    const currentTransform = computedStyle.transform;
    if (currentTransform && currentTransform !== 'none') {
        resetProps.x = 0;
        resetProps.y = 0;
        resetProps.rotation = 0;
        resetProps.scale = 1;
        resetProps.skewX = 0;
        resetProps.skewY = 0;
    }
    
    // Try to reset filter effects
    const currentFilter = computedStyle.filter;
    if (currentFilter && currentFilter !== 'none') {
        resetProps.filter = 'none';
    }
    
    // Try to reset background color
    if (computedStyle.backgroundColor && computedStyle.backgroundColor !== 'rgba(0, 0, 0, 0)') {
        resetProps.backgroundColor = 'transparent';
    }
    
    timeline.to(element, resetProps);
    
    return timeline;
}

/**
 * ✅ FIXED: Animate element to final state using unified system
 */
function animateElementToFinal(elementData) {
    // Basic animations
    const basicEffect = elementData.activeEffects.get('basicAnimations');
    if (basicEffect?.module?.animateToFinal) {
        basicEffect.module.animateToFinal(elementData.element, basicEffect.state);
    }
    
    // Text animations
    const textEffect = elementData.activeEffects.get('textAnimations');
    if (textEffect?.module?.executeAnimation && textEffect.state?.enabled) {
        textEffect.module.executeAnimation(textEffect.state);
    }
}

/**
 * ✅ FIXED: Animate element to initial state using unified system
 */
function animateElementToInitial(elementData) {
    // Basic animations
    const basicEffect = elementData.activeEffects.get('basicAnimations');
    if (basicEffect?.module?.animateToInitial) {
        basicEffect.module.animateToInitial(elementData.element, basicEffect.state);
    }
    
    // ✅ FIXED: Text animations reset for all triggers
    const textEffect = elementData.activeEffects.get('textAnimations');
    if (textEffect?.state?.enabled) {
        if (textEffect.module?.setProgress) {
            // Reset to progress 0 (works for both discrete and progress-based)
            textEffect.module.setProgress(textEffect.state, 0);
        } else if (textEffect.module?.reset) {
            textEffect.module.reset(textEffect.state);
        }
    }
}

/**
 * INTERSECTION TRIGGER - Enhanced with Smart "Once" Logic - ✅ FIXED
 * ✅ ONLY trigger that supports "once" behavior
 * @param {Element} block - DOM element
 * @param {Object} triggerConfig - Trigger configuration  
 * @param {Object} animationConfig - Animation configuration
 * @returns {Object} Trigger controller
 */
export const initIntersectionTrigger = function(block, triggerConfig, animationConfig) {
    const settings = triggerConfig.triggerSettings?.intersection || {};
    
    let currentAnimation = null;
    let hasAnimated = false;
    
    const animateIn = () => {
        if (currentAnimation) currentAnimation.kill();
        hasAnimated = true;
        
        currentAnimation = executeAnimation(block, animationConfig);
        block.classList.add('in-view');
        
        // console.log(`🎬 Intersection animation executed for ${block.id} (once: ${animationConfig.once}) - FIXED VERSION`);
    };
    
    // ✅ FIXED: Use unified system for reverse animation
    const animateOut = () => {
        if (currentAnimation) currentAnimation.kill();
        hasAnimated = false;
        
        // ✅ CRITICAL FIX: Create custom reverse function that uses unified system
        const customReverseConfig = {
            ...animationConfig,
            customReverseAnimationFunction: () => {
                if (window.animationSystem) {
                    const elementData = window.animationSystem.elements.get(block);
                    if (elementData) {
                        // console.log(`🔄 Intersection using unified system reverse for ${block.id}`);
                        animateElementToInitial(elementData);
                        return gsap.timeline();
                    }
                }
                // console.log(`⚠️ Intersection fallback reverse for ${block.id}`);
                return gsap.timeline();
            }
        };
        
        currentAnimation = executeReverseAnimation(block, customReverseConfig);
        block.classList.remove('in-view');
        
        // console.log(`🔄 Intersection animation reversed for ${block.id} - FIXED VERSION`);
    };
    
    const observerCallback = (entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting && (!hasAnimated || !animationConfig.once)) {
                animateIn();
                
                // ✅ SMART: Only disconnect if "once" is true
                if (animationConfig.once) {
                    observer.unobserve(block);
                    // console.log(`✅ Intersection observer disconnected (once: true) for ${block.id}`);
                }
            } else if (!entry.isIntersecting && !animationConfig.once && hasAnimated) {
                animateOut(); // ✅ FIXED: Now properly resets all properties
            }
        });
    };
    
    const observer = new IntersectionObserver(observerCallback, {
        threshold: (settings.threshold || 0) / 100,
        rootMargin: `${-(settings.offset || 0)}px 0px 0px 0px`
    });
    
    block.classList.add('off-trigger-intersection');
    observer.observe(block);
    
    // console.log(`👁️ Intersection trigger initialized for ${block.id} (once: ${animationConfig.once}) - FIXED VERSION`);
    
    return {
        type: 'intersection',
        observer,
        settings,
        destroy: () => {
            observer.disconnect();
            block.classList.remove('off-trigger-intersection', 'in-view');
            if (currentAnimation) currentAnimation.kill();
            // console.log(`🗑️ Intersection trigger destroyed for ${block.id}`);
        }
    };
};

/**
 * SCROLL TRIGGER - ENHANCED (No "Once" Logic)
 * ❌ "Once" removed - scroll has its own animation area logic
 * ✅ Animation area controls start/stop naturally
 * @param {Element} block - DOM element
 * @param {Object} triggerConfig - Trigger configuration
 * @param {Object} animationConfig - Animation configuration
 * @returns {Object} Trigger controller
 */
export const initScrollTrigger = function(block, triggerConfig, animationConfig) {
    const settings = triggerConfig.triggerSettings?.scroll || {};
    
    // console.log(`📜 Scroll trigger initialized for ${block.id} (useAnimationArea: ${settings.useAnimationArea})`);
    
    if (!settings.useAnimationArea) {
        // Simple element-based scroll trigger
        return initElementScrollTrigger(block, settings, animationConfig);
    } else {
        // Complex animation area trigger
        return initAnimationAreaTrigger(block, settings, animationConfig);
    }
};

/**
 * Element-based scroll trigger - ENHANCED (No "Once" Logic)
 * @param {Element} block - DOM element
 * @param {Object} settings - Scroll settings
 * @param {Object} animationConfig - Animation configuration
 * @returns {Object} Trigger controller
 */
function initElementScrollTrigger(block, settings, animationConfig) {
    let currentAnimation = null;
    let hasAnimated = false;
    let isInTriggerZone = false;

    // console.log( settings )
    
    const scrollOffset = settings.scrollTriggerPosition || 0;
    
    const executeAnimationOnScroll = () => {
        if (currentAnimation) currentAnimation.kill();
        hasAnimated = true;
        isInTriggerZone = true;
        
        currentAnimation = executeAnimation(block, animationConfig);
        block.classList.add('scroll-triggered');
        // console.log(`🎬 Element scroll animation executed for ${block.id}`);
    };
    
    const resetAnimationOnScroll = () => {
        if (currentAnimation) currentAnimation.kill();
        hasAnimated = false;
        isInTriggerZone = false;
        
        currentAnimation = executeReverseAnimation(block, animationConfig);
        block.classList.remove('scroll-triggered');
        // console.log(`🔄 Element scroll animation reset for ${block.id}`);
    };
    
    const handleScroll = function() {
        const scrollY = window.scrollY || window.pageYOffset;
        const windowHeight = window.innerHeight;
        const bottomPosition = scrollY + windowHeight;
        
        const rect = block.getBoundingClientRect();
        const elementTop = rect.top + scrollY;
        const triggerPosition = elementTop - scrollOffset;
        
        const shouldBeTriggered = bottomPosition >= triggerPosition;
        
        if (shouldBeTriggered && !isInTriggerZone) {
            executeAnimationOnScroll();
        } else if (!shouldBeTriggered && isInTriggerZone) {
            // ❌ NO "once" CHECK - always reset when leaving trigger zone
            // resetAnimationOnScroll();
        }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state
    
    return {
        type: 'elementScroll',
        settings,
        destroy: () => {
            window.removeEventListener('scroll', handleScroll);
            block.classList.remove('scroll-triggered');
            if (currentAnimation) currentAnimation.kill();
            // console.log(`🗑️ Element scroll trigger destroyed for ${block.id}`);
        }
    };
}

/**
 * Animation area scroll trigger - ENHANCED (Built-in Start/Stop Logic)
 * @param {Element} block - DOM element
 * @param {Object} settings - Scroll settings
 * @param {Object} animationConfig - Animation configuration
 * @returns {Object} Trigger controller
 */
function initAnimationAreaTrigger(block, settings, animationConfig) {
    const calculator = new ScrollProgressCalculator(block, {
        animationStart: settings.animationStart || 0,
        animationEnd: settings.animationEnd || 100,
        scrollDirection: settings.scrollDirection || 'vertical'
    });
    
    let currentAnimation = null;
    let isInAnimationArea = false;
    
    // console.log(`🎯 Animation area trigger for ${block.id} (${settings.animationStart}% - ${settings.animationEnd}%)`);
    
    if (settings.useAnimationArea) {
        // Progress-based animation (scrub mode)
        // console.log(`🎚️ Progress-based scroll animation for ${block.id}`);
        
        calculator.onProgress((progress) => {
            // This integrates with unified system for smooth progress
            if (animationConfig.customAnimationFunction) {
                animationConfig.customAnimationFunction(progress);
            }
        });
        
        // ❌ NO "once" logic for progress-based - it's continuous
        calculator.onEnter(() => {
            block.classList.add('in-animation-area');
            // console.log(`📍 Entered animation area: ${block.id}`);
        });
        
        calculator.onLeave(() => {
            block.classList.remove('in-animation-area');
            // console.log(`📍 Left animation area: ${block.id}`);
        });
        
    } else {
        // Discrete animation on start/complete
        calculator.onStart(() => {
            if (currentAnimation) currentAnimation.kill();
            isInAnimationArea = true;
            
            currentAnimation = executeAnimation(block, animationConfig);
            block.classList.add('animation-started');
            // console.log(`🎬 Animation area start for ${block.id}`);
        });
        
        // ❌ NO "once" CHECK - animation area naturally controls start/stop
        calculator.onLeave(() => {
            if (currentAnimation) currentAnimation.kill();
            isInAnimationArea = false;
            
            currentAnimation = executeReverseAnimation(block, animationConfig);
            block.classList.remove('animation-started');
            // console.log(`🔄 Animation area leave for ${block.id}`);
        });
    }
    
    return {
        type: 'animationArea',
        calculator,
        settings,
        useAnimationArea: settings.useAnimationArea,
        destroy: () => {
            calculator.destroy();
            block.classList.remove('in-animation-area', 'animation-started');
            if (currentAnimation) currentAnimation.kill();
            // console.log(`🗑️ Animation area trigger destroyed for ${block.id}`);
        }
    };
}

/**
 * HOVER TRIGGER - Enhanced (Has Own Repeat Logic) - ✅ FIXED
 * ✅ Has reverseOnLeave instead of "once"
 * @param {Element} block - DOM element
 * @param {Object} triggerConfig - Trigger configuration
 * @param {Object} animationConfig - Animation configuration
 * @returns {Object} Trigger controller
 */
export const initHoverTrigger = function(block, triggerConfig, animationConfig) {
    const settings = triggerConfig.triggerSettings?.hover || {};
    
    let currentAnimation = null;
    let isAnimated = false;
    
    const animateToFinal = () => {
        if (currentAnimation) currentAnimation.kill();
        isAnimated = true;
        currentAnimation = executeAnimation(block, animationConfig);
        block.classList.add('hover-animated');
        // console.log(`🖱️ Hover animate to final for ${block.id} - FIXED VERSION`);
    };
    
    // ✅ FIXED: Use unified system for reverse animation
    const animateToInitial = () => {
        if (currentAnimation) currentAnimation.kill();
        isAnimated = false;
        
        // ✅ CRITICAL FIX: Create custom reverse function that uses unified system
        const customReverseConfig = {
            ...animationConfig,
            customReverseAnimationFunction: () => {
                if (window.animationSystem) {
                    const elementData = window.animationSystem.elements.get(block);
                    if (elementData) {
                        // console.log(`🔄 Hover using unified system reverse for ${block.id}`);
                        animateElementToInitial(elementData);
                        return gsap.timeline();
                    }
                }
                // console.log(`⚠️ Hover fallback reverse for ${block.id}`);
                return gsap.timeline();
            }
        };
        
        currentAnimation = executeReverseAnimation(block, customReverseConfig);
        block.classList.remove('hover-animated');
        // console.log(`🖱️ Hover animate to initial for ${block.id} - FIXED VERSION`);
    };
    
    const handleMouseEnter = () => {
        if (settings.triggerOnMouseEnter) {
            animateToFinal();
        }
    };
    
    const handleMouseLeave = () => {
        if (settings.triggerOnMouseLeave && !isAnimated) {
            animateToFinal();
        } else if (settings.reverseOnLeave && isAnimated) {
            // ✅ HOVER LOGIC: Use reverseOnLeave instead of "once"
            animateToInitial(); // ✅ FIXED: Now properly resets all properties
        }
        // ❌ NO "once" logic - hover is naturally repeatable
    };
    
    block.addEventListener('mouseenter', handleMouseEnter);
    block.addEventListener('mouseleave', handleMouseLeave);
    block.classList.add('off-trigger-hover');
    
    // console.log(`🖱️ Hover trigger initialized for ${block.id} (reverseOnLeave: ${settings.reverseOnLeave}) - FIXED VERSION`);
    
    return {
        type: 'hover',
        settings,
        destroy: () => {
            block.removeEventListener('mouseenter', handleMouseEnter);
            block.removeEventListener('mouseleave', handleMouseLeave);
            block.classList.remove('off-trigger-hover', 'hover-animated');
            if (currentAnimation) currentAnimation.kill();
            // console.log(`🗑️ Hover trigger destroyed for ${block.id}`);
        }
    };
};

// AdvancedTriggers.js - Zastąp TYLKO tę funkcję initClickTrigger
// Reszta pliku zostaje bez zmian!

/**
 * CLICK TRIGGER - Enhanced (Has Own Repeat Logic) - ✅ FIXED FINAL VERSION
 * ✅ Has toggle/reverse instead of "once"
 * @param {Element} block - DOM element
 * @param {Object} triggerConfig - Trigger configuration
 * @param {Object} animationConfig - Animation configuration
 * @returns {Object} Trigger controller
 */
export const initClickTrigger = function(block, triggerConfig, animationConfig) {
    const settings = triggerConfig.triggerSettings?.click || {};
    
    let currentAnimation = null;
    let isAnimated = false;
    let clickCount = 0;
    
    // ✅ DEBUG: Log settings na start
    // console.log(`🖱️ Click trigger settings for ${block.id}:`, {
    //     toggleOnClick: settings.toggleOnClick,
    //     reverseOnSecondClick: settings.reverseOnSecondClick,
    //     fullTriggerConfig: triggerConfig,
    //     fullSettings: settings
    // });
    
    // ✅ HELPER: Create custom reverse function that uses unified system
    const createCustomReverseConfig = () => ({
        ...animationConfig,
        customReverseAnimationFunction: () => {
            if (window.animationSystem) {
                const elementData = window.animationSystem.elements.get(block);
                if (elementData) {
                    // console.log(`🔄 Click using unified system reverse for ${block.id}`);
                    animateElementToInitial(elementData);
                    return gsap.timeline();
                }
            }
            // console.log(`⚠️ Click fallback reverse for ${block.id}`);
            return gsap.timeline();
        }
    });
    
    const handleClick = () => {
        clickCount++;
        // console.log(`🖱️ Click #${clickCount} on ${block.id}, isAnimated: ${isAnimated}`);
        // console.log(`🔧 Mode check: toggleOnClick=${settings.toggleOnClick}, reverseOnSecondClick=${settings.reverseOnSecondClick}`);
        
        if (currentAnimation) currentAnimation.kill();
        
        // ✅ FIXED: Hierarchia logiki z debug logs
        
        if (settings.toggleOnClick === true) {
            // 1. TOGGLE MODE - każdy click przełącza stan
            // console.log('🔄 Using TOGGLE mode');
            if (isAnimated) {
                // Toggle OFF
                isAnimated = false;
                currentAnimation = executeReverseAnimation(block, createCustomReverseConfig());
                block.classList.remove('click-animated');
                // console.log(`🖱️ Toggle OFF for ${block.id}`);
            } else {
                // Toggle ON
                isAnimated = true;
                currentAnimation = executeAnimation(block, animationConfig);
                block.classList.add('click-animated');
                // console.log(`🖱️ Toggle ON for ${block.id}`);
            }
        } 
        else if (settings.reverseOnSecondClick === true) {
            // 2. REVERSE ON SECOND CLICK MODE
            // console.log('🔄 Using REVERSE ON SECOND CLICK mode');
            if (clickCount === 1 && !isAnimated) {
                // Pierwszy click - animuj
                isAnimated = true;
                currentAnimation = executeAnimation(block, animationConfig);
                block.classList.add('click-animated');
                // console.log(`🖱️ First click - animate for ${block.id}`);
            } else if (clickCount >= 2 && isAnimated) {
                // Drugi click - resetuj
                clickCount = 0; // Reset counter
                isAnimated = false;
                currentAnimation = executeReverseAnimation(block, createCustomReverseConfig());
                block.classList.remove('click-animated');
                // console.log(`🖱️ Second click - reverse for ${block.id}`);
            } else {
                // console.log(`🖱️ Ignoring click #${clickCount} in reverse mode (isAnimated: ${isAnimated})`);
            }
        } 
        else {
            // 3. NORMAL MODE - tylko animuj jeśli nie jest już animowany
            // console.log('🔄 Using NORMAL mode');
            if (!isAnimated) {
                isAnimated = true;
                currentAnimation = executeAnimation(block, animationConfig);
                block.classList.add('click-animated');
                // console.log(`🖱️ Normal click animate for ${block.id}`);
            } else {
                // console.log(`🖱️ Already animated, ignoring click for ${block.id}`);
            }
        }
    };
    
    block.addEventListener('click', handleClick);
    block.classList.add('off-trigger-click');
    block.style.cursor = 'pointer';
    
    // console.log(`✅ Click trigger initialized for ${block.id} with settings:`, settings);
    
    return {
        type: 'click',
        settings,
        destroy: () => {
            block.removeEventListener('click', handleClick);
            block.classList.remove('off-trigger-click', 'click-animated');
            block.style.cursor = '';
            if (currentAnimation) currentAnimation.kill();
            // console.log(`🗑️ Click trigger destroyed for ${block.id}`);
        }
    };
};

/**
 * MOUSE POSITION TRIGGER - Delegated to Mobile-Aware Unified System
 * ✅ Now handled by enhanced MouseDistanceModule with mobile fallback
 * ❌ NO "once" logic - distance effects are continuous
 * @param {Element} block - DOM element
 * @param {Object} triggerConfig - Trigger configuration
 * @param {Object} animationConfig - Animation configuration
 * @returns {Object} Trigger controller (delegated)
 */
export const initMousePositionTrigger = function(block, triggerConfig, animationConfig) {
    // console.log(`🧲 Mouse Position trigger delegated to Mobile-Aware Unified System for ${block.id}`);
    
    // This trigger is now handled entirely by the enhanced UnifiedAnimationSystem
    // via MouseDistanceModule with mobile fallback support.
    
    // Initialize global mouse tracking for legacy compatibility
    if (!globalMouseTrackingInitialized) {
        initGlobalMouseTracking();
    }
    
    // ❌ NO "once" logic for mouse position - it's continuous distance-based
    
    // Return minimal controller for API compatibility
    return {
        type: 'mousePosition',
        delegated: true,
        mobileAware: true,
        destroy: () => {
            // console.log(`🗑️ Mouse position trigger cleanup delegated to Unified System`);
            // Cleanup is handled by UnifiedAnimationSystem
        }
    };
};

/**
 * ADVANCED SCROLL TRIGGER ROUTER - Enhanced with Smart Logic
 * @param {Element} block - DOM element
 * @param {Object} triggerConfig - Trigger configuration
 * @param {Object} animationConfig - Animation configuration
 * @returns {Object} Trigger controller
 */
export const initAdvancedScrollTrigger = function(block, triggerConfig, animationConfig) {
    if (!triggerConfig || !triggerConfig.enabled) {
        return null;
    }
    
    const { triggerType } = triggerConfig;
    
    // console.log(`🎯 Initializing advanced trigger: ${triggerType} for ${block.id}`);
    
    // ✅ SMART ROUTING: Each trigger has its own repeat logic
    switch (triggerType) {
        case 'scroll':
            // ❌ NO "once" for scroll - has animation area logic
            return initScrollTrigger(block, triggerConfig, animationConfig);
            
        case 'hover':
            // ✅ Has reverseOnLeave instead of "once"
            return initHoverTrigger(block, triggerConfig, animationConfig);
            
        case 'click':
            // ✅ Has toggle/reverse instead of "once"
            return initClickTrigger(block, triggerConfig, animationConfig);
            
        case 'intersection':
            // ✅ ONLY trigger that uses "once"
            return initIntersectionTrigger(block, triggerConfig, animationConfig);
            
        case 'mousePosition':
            // ❌ NO "once" - continuous distance effects + mobile fallback
            return initMousePositionTrigger(block, triggerConfig, animationConfig);
            
        default:
            console.warn(`Unknown trigger type: ${triggerType}`);
            return null;
    }
};

/**
 * Enhanced Trigger Utils - Updated with Smart Logic Info
 */
export const AdvancedTriggerUtils = {
    getActiveTriggers: () => {
        const triggers = [];
        document.querySelectorAll('.off-advanced-trigger').forEach(block => {
            if (block._triggerEffect) {
                triggers.push({
                    element: block,
                    trigger: block._triggerEffect,
                    type: block._triggerEffect.type,
                    hasOnceLogic: block._triggerEffect.type === 'intersection'
                });
            }
        });
        return triggers;
    },
    
    destroyAllTriggers: () => {
        const triggers = AdvancedTriggerUtils.getActiveTriggers();
        triggers.forEach(({ trigger }) => {
            if (trigger.destroy) trigger.destroy();
        });
        // console.log(`🗑️ Destroyed ${triggers.length} triggers`);
    },
    
    refreshScrollTriggers: () => {
        document.querySelectorAll('.off-advanced-trigger').forEach(block => {
            if (block._progressCalculator) {
                block._progressCalculator.refresh();
            }
        });
        // console.log(`🔄 Refreshed scroll triggers`);
    },
    
    getGlobalMousePosition: () => {
        return { ...globalMousePosition };
    },
    
    // ✅ NEW: Get trigger statistics
    getTriggerStats: () => {
        const triggers = AdvancedTriggerUtils.getActiveTriggers();
        const stats = {
            total: triggers.length,
            byType: {},
            withOnceLogic: 0,
            withoutOnceLogic: 0
        };
        
        triggers.forEach(({ trigger }) => {
            const type = trigger.type;
            stats.byType[type] = (stats.byType[type] || 0) + 1;
            
            if (trigger.type === 'intersection') {
                stats.withOnceLogic++;
            } else {
                stats.withoutOnceLogic++;
            }
        });
        
        return stats;
    }
};

// Export legacy compatibility
export {
    initGlobalMouseTracking,
    globalMousePosition,
    globalMouseTrackingInitialized
};